import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import "./manageaddress.css";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import axios from "axios";
import { baseURL } from "../../constants/url";

const initialFormData = {
  name: "",
  phone_number: "",
  alternate_phone_number: "",
  pincode: "",
  locality: "",
  area: "",
  city: "",
  state: "",
  landmark: "",
  address_type: "home",
};

const ManageAddress = () => {
  const [show, setShow] = useState(false);

  const [selectedOption, setSelectedOption] =
    useState("home");

  const [addresses, setAddresses] = useState([]);

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] =
    useState(initialFormData);

  // =====================================================
  // Fetch addresses
  // =====================================================

  useEffect(() => {
    fetchAddresses();
  }, []);

  // =====================================================
  // Floating label
  // =====================================================

  useEffect(() => {
    const inputs =
      document.querySelectorAll(
        ".manage_addresss input"
      );

    const handlers = [];

    inputs.forEach((input) => {
      if (
        input.type !== "radio" &&
        input.value?.trim() !== ""
      ) {
        input.classList.add("filled");
      }

      const handleInput = () => {
        if (input.type === "radio") return;

        if (input.value.trim() !== "") {
          input.classList.add("filled");
        } else {
          input.classList.remove("filled");
        }
      };

      input.addEventListener(
        "input",
        handleInput
      );

      handlers.push({
        input,
        handleInput,
      });
    });

    return () => {
      handlers.forEach(
        ({ input, handleInput }) => {
          input.removeEventListener(
            "input",
            handleInput
          );
        }
      );
    };
  }, [show]);

  // =====================================================
  // Single field validation
  // =====================================================

  const validateField = (name, value) => {
    let error = "";

    if (name === "phone_number") {
      if (!value) {
        error =
          "Phone number is required";
      } else if (
        !/^[6-9][0-9]{9}$/.test(value)
      ) {
        error =
          "Enter valid 10 digit mobile number";
      }
    }

    if (
      name ===
        "alternate_phone_number" &&
      value !== ""
    ) {
      if (
        !/^[6-9][0-9]{9}$/.test(value)
      ) {
        error =
          "Enter valid alternate mobile number";
      }
    }

    if (name === "pincode") {
      if (!value) {
        error =
          "Pincode is required";
      } else if (
        !/^[1-9][0-9]{5}$/.test(value)
      ) {
        error =
          "Enter valid 6 digit pincode";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    return error;
  };

  // =====================================================
  // Full form validation
  // =====================================================

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name =
        "Name is required";
    }

    if (!formData.phone_number) {
      newErrors.phone_number =
        "Phone number is required";
    } else if (
      !/^[6-9][0-9]{9}$/.test(
        formData.phone_number
      )
    ) {
      newErrors.phone_number =
        "Enter valid 10 digit mobile number";
    }

    if (
      formData.alternate_phone_number &&
      !/^[6-9][0-9]{9}$/.test(
        formData.alternate_phone_number
      )
    ) {
      newErrors.alternate_phone_number =
        "Enter valid alternate mobile number";
    }

    if (!formData.pincode) {
      newErrors.pincode =
        "Pincode is required";
    } else if (
      !/^[1-9][0-9]{5}$/.test(
        formData.pincode
      )
    ) {
      newErrors.pincode =
        "Enter valid 6 digit pincode";
    }

    if (!formData.locality.trim()) {
      newErrors.locality =
        "Locality is required";
    }

    if (!formData.area.trim()) {
      newErrors.area =
        "Address is required";
    }

    if (!formData.city.trim()) {
      newErrors.city =
        "City is required";
    }

    if (!formData.state.trim()) {
      newErrors.state =
        "State is required";
    }

    setErrors(newErrors);

    return (
      Object.keys(newErrors).length ===
      0
    );
  };

  // =====================================================
  // Input change
  // =====================================================

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    if (name === "pincode") {
      value = value
        .replace(/\D/g, "")
        .slice(0, 6);
    }

    if (
      name === "phone_number" ||
      name ===
        "alternate_phone_number"
    ) {
      value = value
        .replace(/\D/g, "")
        .slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    validateField(name, value);
  };

  // =====================================================
  // Address type
  // =====================================================

  const handleAddressTypeChange = (
    e
  ) => {
    const value = e.target.value;

    setSelectedOption(value);

    setFormData((prev) => ({
      ...prev,
      address_type: value,
    }));
  };

  // =====================================================
  // Get saved addresses
  // =====================================================

  const fetchAddresses = async () => {
    try {
      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        return;
      }

      const res = await axios.get(
        `${baseURL}common/addresses/`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      console.log(
        "Address GET response:",
        res.data
      );

      // Works for both paginated and normal array response
      const addressList =
        res.data?.results ||
        res.data ||
        [];

      setAddresses(
        Array.isArray(addressList)
          ? addressList
          : []
      );
    } catch (error) {
      console.error(
        "Failed to fetch addresses:",
        error?.response?.data ||
          error.message
      );

      setAddresses([]);
    }
  };

  // =====================================================
  // Save address
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Invalid Input",
        text: "Please fill all required fields correctly.",
      });

      return;
    }

    try {
      setLoading(true);

      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        Swal.fire({
          icon: "warning",
          title: "Login Required",
          text: "Please login first.",
        });

        return;
      }

      /*
       * IMPORTANT:
       * user: 1 removed.
       *
       * Backend should take logged-in user
       * from JWT token.
       */
      const payload = {
        name:
          formData.name.trim(),

        phone_number:
          formData.phone_number,

        alternate_phone_number:
          formData.alternate_phone_number,

        pincode:
          formData.pincode,

        locality:
          formData.locality.trim(),

        area:
          formData.area.trim(),

        city:
          formData.city.trim(),

        state:
          formData.state.trim(),

        landmark:
          formData.landmark.trim(),

        address_type:
          formData.address_type,
      };

      console.log(
        "Address POST Payload:",
        payload
      );

      const response =
        await axios.post(
          `${baseURL}common/addresses/`,
          payload,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,

              "Content-Type":
                "application/json",
            },
          }
        );

      console.log(
        "Address Saved Response:",
        response.data
      );

      // Usually POST create gives 201
      if (
        response.status === 200 ||
        response.status === 201
      ) {
        await Swal.fire({
          icon: "success",
          title: "Address Saved!",
          text: "Address saved successfully!",
          confirmButtonColor:
            "#3085d6",
          confirmButtonText: "OK",
        });

        // Close form
        setShow(false);

        // Reset form
        setFormData(
          initialFormData
        );

        setSelectedOption(
          "home"
        );

        setErrors({});

        // Reload addresses from DB
        await fetchAddresses();
      }
    } catch (error) {
      console.error(
        "Address Save Error:",
        error
      );

      console.error(
        "Backend Response:",
        error?.response?.data
      );

      const backendErrors =
        error?.response?.data;

      if (
        backendErrors &&
        typeof backendErrors ===
          "object"
      ) {
        const apiErrors = {};

        Object.keys(
          backendErrors
        ).forEach((key) => {
          const value =
            backendErrors[key];

          apiErrors[key] =
            Array.isArray(value)
              ? value[0]
              : value;
        });

        setErrors((prev) => ({
          ...prev,
          ...apiErrors,
        }));
      }

      const message =
        error?.response?.data
          ?.detail ||
        error?.response?.data
          ?.message ||
        "Failed to save address.";

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: message,
        confirmButtonColor:
          "#e83333",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // Delete
  // =====================================================

  const handleDelete = async (id) => {
    const result =
      await Swal.fire({
        title: "Are you sure?",
        text: "Are you sure you want to delete this address?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor:
          "#d33",
        cancelButtonColor:
          "#e83333",
        confirmButtonText:
          "Yes, delete it!",
        cancelButtonText:
          "Cancel",
      });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const token =
        localStorage.getItem(
          "authToken"
        );

      await axios.delete(
        `${baseURL}common/addresses/${id}/`,
        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Address deleted successfully.",
        confirmButtonColor:
          "#3085d6",
      });

      fetchAddresses();
    } catch (error) {
      console.error(
        "Failed to delete address:",
        error?.response?.data ||
          error.message
      );

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Failed to delete address.",
        confirmButtonColor:
          "#3085d6",
      });
    }
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="card_common">

      <div>
        <button
          type="button"
          className="common_btn_all"
          onClick={() =>
            setShow(
              (prev) => !prev
            )
          }
        >
          <AddIcon />

          ADD A NEW ADDRESS
        </button>
      </div>

      {/* Add Address Form */}

      {show && (
        <div className="pl_40 pt_40 pb_40">

          <div className="heading_common mt_15">
            <h3>
              Delivery Address
            </h3>
          </div>

          <form
            onSubmit={
              handleSubmit
            }
            className="manage_addresss"
          >

            {[
              [
                "name",
                "Name",
              ],

              [
                "phone_number",
                "Phone Number",
              ],

              [
                "pincode",
                "Pin Code",
              ],

              [
                "locality",
                "Locality",
              ],

              [
                "area",
                "Address",
              ],

              [
                "city",
                "City/District/Town",
              ],

              [
                "state",
                "State",
              ],

              [
                "landmark",
                "Landmark (Optional)",
              ],

              [
                "alternate_phone_number",
                "Alternate Phone Number",
              ],
            ].map(
              ([
                field,
                label,
              ]) => (

                <div
                  className={
                    field ===
                    "area"
                      ? "inputSection"
                      : "inputSectionSplit"
                  }
                  key={
                    field
                  }
                >

                  <input
                    type="text"
                    name={
                      field
                    }
                    inputMode={
                      field ===
                        "pincode" ||
                      field ===
                        "phone_number" ||
                      field ===
                        "alternate_phone_number"
                        ? "numeric"
                        : undefined
                    }
                    maxLength={
                      field ===
                      "pincode"
                        ? 6
                        : [
                              "phone_number",
                              "alternate_phone_number",
                            ].includes(
                              field
                            )
                          ? 10
                          : undefined
                    }
                    value={
                      formData[
                        field
                      ]
                    }
                    onChange={
                      handleInputChange
                    }
                  />

                  <label className="inputLabel">
                    {label}
                  </label>

                  {errors[
                    field
                  ] && (

                    <small
                      style={{
                        color:
                          "red",
                      }}
                    >
                      {
                        errors[
                          field
                        ]
                      }
                    </small>

                  )}

                </div>
              )
            )}

            {/* Address Type */}

            <div className="color_grayDark">

              <p>
                Address Type
              </p>

              <div className="d_flex align_center gap_20">

                {[
                  "home",
                  "work",
                ].map(
                  (type) => (

                    <label
                      key={
                        type
                      }
                      className="d_flex align_center gap_5"
                    >

                      <input
                        type="radio"
                        name="address_type"
                        value={
                          type
                        }
                        checked={
                          selectedOption ===
                          type
                        }
                        onChange={
                          handleAddressTypeChange
                        }
                      />

                      {type
                        .charAt(
                          0
                        )
                        .toUpperCase() +
                        type.slice(
                          1
                        )}

                    </label>

                  )
                )}

              </div>

              <div className="mb_20">

                <button
                  type="submit"
                  className="saveForm"
                  disabled={
                    loading
                  }
                >

                  {loading
                    ? "Saving..."
                    : "Save"}

                </button>

              </div>

            </div>

          </form>

        </div>
      )}

      {/* Saved Addresses */}

      {addresses.length > 0 ? (

        addresses.map(
          (addr) => (

            <div
              key={
                addr.id
              }
              className="address_set mt_20 padd_10 border_gray"
            >

              <div className="address_set_in">

                <div className="d_flex align_center justify_sp_between">

                  <span className="address-type">
                    {
                      addr.address_type
                    }
                  </span>

                  <div className="edit_delete_address">

                    <button
                      type="button"
                      className="cross_btn"
                      onClick={() =>
                        handleDelete(
                          addr.id
                        )
                      }
                    >

                      <DeleteIcon />

                    </button>

                  </div>

                </div>

                <div className="address_set_in_print">

                  <h6>
                    {addr.name}{" "}
                    {
                      addr.phone_number
                    }
                  </h6>

                  <p>
                    {addr.area}
                    {addr.locality
                      ? `, ${addr.locality}`
                      : ""}

                    {addr.city
                      ? `, ${addr.city}`
                      : ""}

                    {addr.state
                      ? `, ${addr.state}`
                      : ""}

                    {addr.pincode
                      ? ` - ${addr.pincode}`
                      : ""}

                    {addr.landmark
                      ? `, Landmark: ${addr.landmark}`
                      : ""}
                  </p>

                  {addr.alternate_phone_number && (
                    <p>
                      Alternate
                      Phone:{" "}
                      {
                        addr.alternate_phone_number
                      }
                    </p>
                  )}

                </div>

              </div>

            </div>

          )
        )

      ) : (

        <p className="mt_20">
          No address found.
        </p>

      )}

    </div>
  );
};

export default ManageAddress;